// PR-27: Web Speech API hook for mobile Capture tab voice input.
//
// Browser-native, zero backend cost. Supports Chrome / Safari (and most
// Chromium derivatives via webkit-prefixed `webkitSpeechRecognition`).
// Firefox / in-app browsers without the API report `supported: false`
// — the caller hides the mic button entirely.
//
// Continuous mode + interim results = the live transcript shown above
// the textarea while the user is talking. On stop, the *final* chunks
// are appended to the existing text input by the consumer (we keep them
// separate from interim so the consumer can decide when to commit).
//
// Phase C TODO: a per-thread `lang` override and a Whisper backend
// fallback for unsupported browsers (PR-27.5) — see MobileCapture.tsx.

import { useEffect, useRef, useState } from 'react'

function getCtor(): any | null {
  if (typeof window === 'undefined') return null
  return (
    (window as any).SpeechRecognition ??
    (window as any).webkitSpeechRecognition ??
    null
  )
}

export interface UseSpeechRecognitionResult {
  /** True if the browser exposes a usable Web Speech API. */
  supported: boolean
  /** Currently capturing audio. */
  recording: boolean
  /** Live interim transcript while recording (cleared on stop / reset). */
  interim: string
  /** Last fully-finalized transcript chunk (resets on next start / reset). */
  final: string
  /** Last error code, if any. */
  error: string | null
  start: () => void
  stop: () => void
  reset: () => void
}

export function useSpeechRecognition(opts?: {
  lang?: string
}): UseSpeechRecognitionResult {
  const Ctor = getCtor()
  const supported = !!Ctor
  const recRef = useRef<any>(null)
  const [recording, setRecording] = useState(false)
  const [interim, setInterim] = useState('')
  const [final, setFinal] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Lang heuristic: navigator.language → fallback "en-US". Phase C
  // i18n could expose a per-thread lang override.
  const lang =
    opts?.lang ??
    (typeof navigator !== 'undefined' ? navigator.language : 'en-US')

  useEffect(() => {
    if (!supported) return
    const r = new Ctor()
    r.continuous = true
    r.interimResults = true
    r.lang = lang
    r.onresult = (event: any) => {
      let interimAcc = ''
      let finalAcc = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) finalAcc += result[0].transcript
        else interimAcc += result[0].transcript
      }
      setInterim(interimAcc)
      if (finalAcc) setFinal((prev) => (prev + ' ' + finalAcc).trim())
    }
    r.onerror = (event: any) => {
      const code = event?.error
      // 'no-speech' fires after a few seconds of silence — not really an
      // error, just stop quietly.
      if (code === 'no-speech') {
        setRecording(false)
        return
      }
      setError(code ?? 'unknown')
      setRecording(false)
    }
    r.onend = () => setRecording(false)
    recRef.current = r
    return () => {
      try {
        r.stop()
      } catch {
        // already stopped or never started — fine
      }
      recRef.current = null
    }
  }, [supported, Ctor, lang])

  const start = () => {
    if (!recRef.current || recording) return
    setError(null)
    setInterim('')
    setFinal('')
    try {
      recRef.current.start()
      setRecording(true)
    } catch (e) {
      setError((e as Error).message)
    }
  }
  const stop = () => {
    if (!recRef.current) return
    try {
      recRef.current.stop()
    } catch {
      // already stopped — fine
    }
  }
  const reset = () => {
    setInterim('')
    setFinal('')
    setError(null)
  }

  return { supported, recording, interim, final, error, start, stop, reset }
}
